import { API_BASE } from "./serverBase.js";

/**
 * Are we running inside the packaged iOS/Android shell rather than a browser?
 *
 * This matters for exactly one thing: selling. Apple and Google require their
 * own in-app purchase system for digital goods bought inside an app, and take
 * 15–30% for it. So the native builds show the whole wardrobe — everything a
 * player owns, everything they can equip — but no buy buttons. Purchases
 * happen on the website, and the items show up in the app afterwards.
 *
 * Two independent signals, because neither is guaranteed on its own:
 *
 * - Capacitor injects a `Capacitor` global into the WebView. It is the direct
 *   answer, but only present once the native bridge has initialised.
 * - The mobile bundle is built with VITE_API_BASE pointing at the production
 *   server (a web build leaves it empty), so a non-empty API base means this
 *   bundle was made for a shell.
 */
type CapacitorGlobal = { isNativePlatform?: () => boolean; getPlatform?: () => string };

export function isNativeApp(): boolean {
  if (typeof window === "undefined") return false;
  const capacitor = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  if (capacitor) {
    if (typeof capacitor.isNativePlatform === "function") return capacitor.isNativePlatform();
    const platform = capacitor.getPlatform?.();
    if (platform && platform !== "web") return true;
  }
  return API_BASE !== "";
}

/**
 * Whether this build may show prices and buy buttons. Kept as its own name so
 * call sites read as intent ("can I sell here?") rather than as a platform
 * check that someone might later reuse for layout.
 */
export function canShowPurchases(): boolean {
  return !isNativeApp();
}
